// ✅ src/pages/VehicleV2/OwnVehicleAverageMileageReport_Print.jsx
// Print page UI like your template + matches old ASPX (Crystal) screenshot
// - reads filters from navigate state OR query string
// - calls GET /api/ownVehicleAverageMileageReport/list
// - renders header + title + from/to + table

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/averageMillageReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const pad2 = (n) => String(n).padStart(2, "0");

const fmtDDMMMYYYY = (dtLike) => {
  if (!dtLike) return "";
  const d = new Date(dtLike);
  if (Number.isNaN(d.getTime())) return toStr(dtLike);

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${pad2(d.getDate())}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const parseDDMMYYYY_toISO = (ddmmyyyy) => {
  // "dd/MM/yyyy" -> "yyyy-MM-dd"
  const s = toStr(ddmmyyyy);
  if (!s) return "";
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const fmtForHeader = (fromDateDDMM, toDateDDMM) => {
  // Screenshot header shows "16-Feb-2025" style (dd-MMM-yyyy)
  const fromISO = parseDDMMYYYY_toISO(fromDateDDMM);
  const toISO = parseDDMMYYYY_toISO(toDateDDMM);
  return {
    fromText: fromISO ? fmtDDMMMYYYY(fromISO) : toStr(fromDateDDMM),
    toText: toISO ? fmtDDMMMYYYY(toISO) : toStr(toDateDDMM),
  };
};

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

export default function OwnVehicleAverageMileageReport_Print() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [sp] = useSearchParams();

  // ✅ support BOTH:
  // 1) navigate("/print", { state:{ filters }})
  // 2) open /print?fromDate=&toDate=&fuelType=&branchId=
  const filters = useMemo(() => {
    const f = state?.filters || {};
    return {
      branchId: toStr(f.branchId || sp.get("branchId") || ""),
      branchType: toStr(f.branchType || sp.get("branchType") || ""),
      loginUserId: toStr(f.loginUserId || sp.get("loginUserId") || ""),
      fuelType: toStr(f.fuelType || sp.get("fuelType") || "Diesel"),
      fromDate: toStr(f.fromDate || sp.get("fromDate") || ""),
      toDate: toStr(f.toDate || sp.get("toDate") || ""),
    };
  }, [state, sp]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  const headerDates = useMemo(() => {
    return fmtForHeader(filters.fromDate, filters.toDate);
  }, [filters.fromDate, filters.toDate]);

  const currentDate = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });
  }, []);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const params = {
        branchId: filters.branchId,
        branchType: filters.branchType,
        loginUserId: filters.loginUserId,
        fuelType: filters.fuelType,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      };

      const { data } = await axios.get(`${API_BASE}/list`, { params });

      if (!data?.success) throw new Error(data?.message || "Failed to load report");
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (e) {
      setRows([]);
      setErr(e?.response?.data?.message || e?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportTitle = useMemo(() => {
    // matches screenshot title
    // "Own Vehicles Average Milage/Diesel Quantity"
    const fuelLabel = filters.fuelType?.toLowerCase() === "petrol" ? "Petrol" : "Diesel";
    return `Own Vehicles Average Milage/${fuelLabel} Quantity`;
  }, [filters.fuelType]);

  return (
    <div className="max-w-5xl mx-auto bg-white print:max-w-none print:mx-0">
      {/* ✅ Print CSS like your template */}
      <style>{`
        @media print {
          body { margin: 0; padding: 16px; font-size: 12pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; }
          th, td { border: 1px solid #000; padding: 6px; }
        }
        @page { size: A4 portrait; margin: 10mm; }
      `}</style>

      <div className="p-6 print:p-4">
        {/* Top actions */}
        <div className="no-print flex justify-between items-center mb-4">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 border rounded-lg hover:bg-gray-100"
          >
            ⬅ Back
          </button>

          <div className="flex gap-2">
            <button
              onClick={() => window.print()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow"
            >
              🖨️ Print
            </button>
          </div>
        </div>

        {/* Header box (like screenshot + template) */}
        <div className="border-2 border-gray-800 mb-4 print:border-black">
          <div className="flex items-center p-4 print:p-3">
            {/* Logo area */}
            <div className="w-24 h-20 flex items-center justify-center mr-6 print:w-20 print:h-16 print:mr-4">
              {/* Replace with your logo image if you have */}
              <div className="w-full h-full border border-gray-400 flex items-center justify-center">
                <span className="text-xs text-gray-500 font-semibold">LOGO</span>
              </div>
            </div>

            {/* Company info (match your template text) */}
            <div className="flex-1 text-center">
              <h1 className="text-2xl font-bold text-gray-800 mb-1 print:text-xl">
                Nissi Infotech
              </h1>
              <h2 className="text-base font-semibold text-gray-700 mb-1 print:text-sm">
                Nissi <span className="uppercase">Civil Construction</span>
              </h2>
              <div className="text-sm text-gray-700 leading-5 print:text-xs">
                <div>3/42-12, Nagappa Colony, kavundampalayam - 641030 . Tamil Nadu . India .</div>
                <div>Phone No :9003744644</div>
                <div className="text-xs">infracnsoft-implementation@nissiinfotech.in</div>
              </div>
            </div>

            {/* Report date */}
            <div className="text-right text-sm print:text-xs min-w-[120px]">
              <p className="font-semibold">Report Date:</p>
              <p>{currentDate}</p>
            </div>
          </div>
        </div>

        {/* Screen id line (like ASPX B-44/1) */}
        <div className="text-right text-xs text-gray-700 mb-2">
          <span className="font-semibold">INFRA SCREEN :</span> B-44/1
        </div>

        {/* Title */}
        <div className="text-center mb-3">
          <h3 className="text-lg font-bold text-gray-800 print:text-base">
            {reportTitle}
          </h3>
        </div>

        {/* From / To (like screenshot row) */}
        <div className="border border-gray-800 mb-3 print:border-black">
          <div className="grid grid-cols-2 text-sm print:text-xs">
            <div className="px-3 py-2 border-r border-gray-800 print:border-black">
              <span className="font-semibold">From Date :</span>{" "}
              {headerDates.fromText || "-"}
            </div>
            <div className="px-3 py-2">
              <span className="font-semibold">To Date :</span>{" "}
              {headerDates.toText || "-"}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mb-4">
          <table className="w-full border-collapse border-2 border-gray-800 print:border-black">
            <thead>
              <tr className="bg-gray-200 text-black print:bg-gray-200">
                <th className="border border-gray-800 px-2 py-2 text-center font-bold print:border-black w-16">
                  S.No
                </th>
                <th className="border border-gray-800 px-3 py-2 text-center font-bold print:border-black">
                  Vehicle Name
                </th>
                <th className="border border-gray-800 px-3 py-2 text-center font-bold print:border-black">
                  Vehicle Code
                </th>
                <th className="border border-gray-800 px-3 py-2 text-center font-bold print:border-black">
                  Vehicle Number
                </th>
                <th className="border border-gray-800 px-3 py-2 text-center font-bold print:border-black">
                  Vehicle Basis
                </th>
                <th className="border border-gray-800 px-3 py-2 text-center font-bold print:border-black">
                  Avg Milage/Fuel Per Hr
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="border border-gray-300 px-3 py-6 text-center print:border-black">
                    Loading...
                  </td>
                </tr>
              ) : err ? (
                <tr>
                  <td colSpan={6} className="border border-gray-300 px-3 py-6 text-center text-red-600 print:border-black">
                    {err}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="border border-gray-300 px-3 py-6 text-center print:border-black">
                    No Records Found
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => {
                  const name = pick(r, ["fld_vehicle_name", "vehicle_name"]);
                  const code = pick(r, ["fld_vehicle_code", "vehicle_code"]);
                  const regno = pick(r, ["fld_vehicle_regno", "vehicle_regno", "fld_vehicle_no"]);
                  const basis = pick(r, ["fld_vehicle_basistype", "vehicle_basistype"]);
                  const mileage = pick(r, ["mileage", "avgMileage"]);

                  return (
                    <tr key={idx} className="print:bg-white">
                      <td className="border border-gray-300 px-2 py-2 text-center font-medium print:border-black">
                        {idx + 1}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 print:border-black">
                        {name}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 print:border-black">
                        {code}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 print:border-black">
                        {regno}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 print:border-black">
                        {basis}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center print:border-black">
                        {String(mileage ?? "")}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer like template */}
        <div className="mt-8 print:mt-6">
          <div className="flex justify-between items-end">
            <div className="text-sm print:text-xs">
              <p className="font-semibold mb-1">Report Generated By:</p>
              <p>Nissi Infotech System</p>
              <p className="text-gray-600">Automated Report</p>
            </div>

            <div className="text-right">
              <div className="border-t border-gray-400 pt-2 mt-12 w-48">
                <p className="text-sm font-semibold print:text-xs">Authorized Signature</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom note */}
        <div className="mt-6 text-center text-xs text-gray-500 print:text-black print:mt-4">
          <p>This is a computer-generated report. No signature is required for authenticity.</p>
        </div>
      </div>
    </div>
  );
}
